// InlineEdit — 범용 인라인 편집 wrapper (U43).
//
// 기본 상태: 값을 텍스트로 표시.
// 클릭 → input/textarea 전환.
// Enter(single) 또는 Shift+Enter(multiline 줄바꿈) → 저장.
// Esc → 취소.
// blur → 변경 있을 때만 저장.
// 저장 중: 우상단 작은 Loader2 스피너.
// 저장 후: "저장됨" 메시지 1.5초 fade-out.
// validate 실패 시: 빨간 테두리 + 에러 메시지.
// password 타입: •••• 마스킹 + 눈 토글.

import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
  type FocusEvent,
} from 'react';
import { Loader2, Eye, EyeOff } from 'lucide-react';

// ─── 타입 ─────────────────────────────────────────────────────

/** InlineEdit가 지원하는 필드 타입 */
export type InlineEditType =
  | 'text'
  | 'password'
  | 'textarea'
  | 'number'
  | 'email';

export interface InlineEditProps {
  /** 현재 표시 값 */
  value: string;
  /**
   * 저장 콜백 — async 가능.
   * resolve: 저장 성공 → 편집 모드 종료.
   * reject: 저장 실패 → 에러 상태 유지.
   */
  onSave: (newValue: string) => Promise<void> | void;
  /** 필드 타입 (기본 'text') */
  type?: InlineEditType;
  /** placeholder 텍스트 */
  placeholder?: string;
  /** 멀티라인 여부 (textarea와 동일 효과) */
  multiline?: boolean;
  /**
   * 유효성 검사 함수.
   * false 반환 시 저장 불가, 빨간 테두리.
   * string 반환 시 에러 메시지로 표시.
   */
  validate?: (value: string) => boolean | string;
  /** 추가 className */
  className?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 표시 전용 레이블 (screen reader용) */
  ariaLabel?: string;
}

// ─── 내부 상수 ────────────────────────────────────────────────

/** password 계열로 마스킹 처리할 필드 타입 */
const IS_PASSWORD_TYPE: Record<InlineEditType, boolean> = {
  text: false,
  password: true,
  textarea: false,
  number: false,
  email: false,
};

/** textarea 계열 — multiline 줄바꿈 지원 */
function isMultiline(type: InlineEditType, multilineProp?: boolean): boolean {
  return type === 'textarea' || multilineProp === true;
}

// ─── 컴포넌트 ─────────────────────────────────────────────────

/**
 * InlineEdit — 클릭 시 input/textarea로 전환되는 인라인 편집 컴포넌트.
 * 저장 상태(spinner) + 저장 완료 피드백("저장됨" 1.5초) 내장.
 */
export function InlineEdit({
  value,
  onSave,
  type = 'text',
  placeholder = '',
  multiline: multilineProp,
  validate,
  className = '',
  disabled = false,
  ariaLabel,
}: InlineEditProps) {
  // 편집 모드 여부
  const [editing, setEditing] = useState(false);
  // 편집 중 임시 값
  const [draft, setDraft] = useState(value);
  // 저장 진행 중
  const [saving, setSaving] = useState(false);
  // 저장 완료 피드백 ("저장됨")
  const [saved, setSaved] = useState(false);
  // 에러 메시지 (validate 실패 or 서버 오류)
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // password 타입 보기/숨기기 토글
  const [showPassword, setShowPassword] = useState(false);
  // password 타입 — 표시 모드에서 편집 없이 값만 보기 (눈 버튼)
  const [revealed, setRevealed] = useState(false);

  // 입력 요소 ref (input 또는 textarea)
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 저장됨 타이머 ref
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMultilineMode = isMultiline(type, multilineProp);
  const isPasswordMode = IS_PASSWORD_TYPE[type];

  // 편집 모드 진입 시 draft 동기화 + 포커스
  useEffect(() => {
    if (!editing) return;
    setDraft(value);
    setErrorMsg(null);
    // 마이크로태스크 뒤 포커스 (렌더 완료 후)
    const t = setTimeout(() => {
      if (isMultilineMode) {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      } else {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }, 0);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // ─── 저장 실행 ─────────────────────────────────────────────

  const commit = async () => {
    if (saving) return;

    const trimmed = isPasswordMode ? draft : draft.trim();

    // 변경 없으면 취소로 처리
    if (trimmed === value) {
      setEditing(false);
      return;
    }

    // 유효성 검사
    if (validate) {
      const result = validate(trimmed);
      if (result === false) {
        setErrorMsg('입력값이 유효하지 않습니다.');
        return;
      }
      if (typeof result === 'string') {
        setErrorMsg(result);
        return;
      }
    }

    setSaving(true);
    setErrorMsg(null);
    try {
      await onSave(trimmed);
      setEditing(false);
      // 저장됨 피드백 1.5초
      setSaved(true);
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '저장 실패';
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  // ─── 키보드 핸들러 ─────────────────────────────────────────

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setEditing(false);
      setErrorMsg(null);
      return;
    }

    if (e.key === 'Enter') {
      if (isMultilineMode) {
        // multiline: Shift+Enter는 줄바꿈 (기본 동작 허용), Enter만 → 저장
        if (!e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          void commit();
        }
        // shiftKey → 기본 동작(줄바꿈) 허용
      } else {
        e.preventDefault();
        e.stopPropagation();
        void commit();
      }
    }
  };

  // blur → 변경 있을 때만 저장
  const handleBlur = (_e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (saving) return;
    void commit();
  };

  // 표시 클릭 → 편집 모드 진입
  const handleDisplayClick = () => {
    if (disabled) return;
    setEditing(true);
    setShowPassword(false);
  };

  // ─── 클래스 조합 ───────────────────────────────────────────

  const wrapClass = [
    'inline-edit',
    errorMsg ? 'inline-edit--error' : '',
    saving ? 'inline-edit--saving' : '',
    disabled ? 'inline-edit--disabled' : '',
    // 단일행 필드는 CSS에서 한 줄 고정 + 말줄임 처리
    isMultilineMode ? 'inline-edit--multiline' : 'inline-edit--single',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // ─── 편집 중 렌더 ──────────────────────────────────────────

  if (editing) {
    const commonProps = {
      className: `inline-edit__input${errorMsg ? ' inline-edit__input--error' : ''}`,
      value: draft,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDraft(e.target.value);
        if (errorMsg) setErrorMsg(null);
      },
      onBlur: handleBlur,
      disabled: saving,
      placeholder,
      'aria-label': ariaLabel ?? '편집',
      'aria-invalid': errorMsg ? ('true' as const) : undefined,
      // DnD 이벤트 차단
      onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    };

    return (
      <span className={wrapClass}>
        {isMultilineMode ? (
          <textarea
            ref={textareaRef}
            {...commonProps}
            onKeyDown={handleKeyDown as (e: KeyboardEvent<HTMLTextAreaElement>) => void}
            rows={3}
          />
        ) : (
          <span className="inline-edit__input-row">
            <input
              ref={inputRef}
              {...commonProps}
              type={isPasswordMode && !showPassword ? 'password' : type === 'number' ? 'number' : type === 'email' ? 'email' : 'text'}
              onKeyDown={handleKeyDown as (e: KeyboardEvent<HTMLInputElement>) => void}
            />
            {/* password 타입: 눈 토글 */}
            {isPasswordMode && (
              <button
                type="button"
                className="inline-edit__eye-btn"
                onMouseDown={(e) => {
                  e.preventDefault(); // blur 방지
                  setShowPassword((s) => !s);
                }}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
          </span>
        )}

        {/* 저장 중 스피너 */}
        {saving && (
          <Loader2
            size={12}
            className="inline-edit__spinner animate-spin"
            aria-label="저장 중"
          />
        )}

        {/* 에러 메시지 */}
        {errorMsg && (
          <span className="inline-edit__error" role="alert">
            {errorMsg}
          </span>
        )}
      </span>
    );
  }

  // ─── 표시 모드 렌더 ────────────────────────────────────────

  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <span className={wrapClass}>
      <span
        className={`inline-edit__display${isEmpty ? ' inline-edit__display--empty' : ''}${disabled ? '' : ' inline-edit__display--clickable'}`}
        onClick={handleDisplayClick}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleDisplayClick();
          }
        }}
        aria-label={ariaLabel ?? '클릭하여 편집'}
        // 말줄임으로 잘린 값 확인용 툴팁 (가려진 비밀번호는 노출 금지)
        title={(!isPasswordMode || revealed) && !isEmpty ? value : undefined}
      >
        {isPasswordMode && !isEmpty && !revealed
          ? '•'.repeat(Math.min(12, value.length))
          : displayValue}
      </span>

      {/* password 표시 모드 — 편집 진입 없이 값 보기/숨기기 */}
      {isPasswordMode && !isEmpty && (
        <button
          type="button"
          className={`inline-edit__eye-btn${revealed ? ' inline-edit__eye-btn--on' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setRevealed((r) => !r);
          }}
          aria-label={revealed ? '비밀번호 숨기기' : '비밀번호 보기'}
          aria-pressed={revealed}
          title={revealed ? '숨기기' : '보기'}
        >
          {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      )}

      {/* 저장됨 피드백 */}
      {saved && (
        <span className="inline-edit__saved" aria-live="polite">
          저장됨
        </span>
      )}
    </span>
  );
}
